import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { sendVerificationCode, resetPasswordWithCode } from '../firebase/emailVerification';
import { useT } from '../i18n';
import AuthLayout from '../components/AuthLayout';
import PhoneAuthPanel, { MODES as PHONE_MODES } from '../components/PhoneAuthPanel';
import './auth.css';

const RESEND_COOLDOWN_S = 60;

// Returns { level 0-5, color }. Label looked up via t() in render. Mirrors
// RegisterForm's copy of the same small helper — this codebase keeps these
// per-auth-page rather than sharing a utils module (see e.g. getFirebaseError
// below, also duplicated per page).
function getPasswordStrength(pw) {
  if (!pw)          return { level: 0, color: '#e5e7eb' };
  if (pw.length < 6) return { level: 1, color: '#ef4444' };
  let score = 1;
  if (pw.length >= 8)           score++;
  if (/[A-Z]/.test(pw))        score++;
  if (/[0-9]/.test(pw))        score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 2) return { level: 2, color: '#f97316' };
  if (score === 3) return { level: 3, color: '#eab308' };
  if (score === 4) return { level: 4, color: '#22c55e' };
  return              { level: 5, color: '#10b981' };
}

const STRENGTH_KEYS = ['', 'auth_pw_tooshort', 'auth_pw_weak', 'auth_pw_fair', 'auth_pw_good', 'auth_pw_strong'];

function EyeOpen() {
  return (
    <svg viewBox="0 0 24 24" fill="none" width="18" height="18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  );
}

function EyeClosed() {
  return (
    <svg viewBox="0 0 24 24" fill="none" width="18" height="18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

// Two real steps, same reasoning as RegisterForm:
//   1. email    — request a 6-digit code (server checks the account exists).
//   2. reset    — code + new password, submitted TOGETHER in one call.
// Step 2 is deliberately one combined submit, not "verify the code" then a
// separate "change password" — see resetPasswordWithCode's own comment for
// why splitting a password change (an EXISTING account, unlike registration)
// into two calls would be insecure.
export default function ForgotPassword() {
  const { t } = useT();

  const navigate = useNavigate();
  // ── THE OTHER WAY TO PROVE WHO YOU ARE ─────────────────────────────────────
  // A phone account has no email to send a code to, so the email form below is
  // a dead end for anyone who signed up with a number. Confirming an SMS code
  // proves ownership just as well, and it leaves the reader signed in — so the
  // "reset" is simply: prove the number, then choose a password.
  //
  // Not a separate route: it is the same task, and /forgot-password is where
  // both /login and /register already point.
  const [usePhone, setUsePhone] = useState(false);
  const [step, setStep] = useState('email'); // 'email' | 'reset' | 'done'
  const [email,        setEmail]        = useState('');
  const [code,          setCode]         = useState('');
  const [newPassword,   setNewPassword]  = useState('');
  const [confirm,       setConfirm]      = useState('');
  const [showPass,      setShowPass]     = useState(false);
  const [showConfirm,   setShowConfirm]  = useState(false);
  const [sending,       setSending]      = useState(false);
  const [resetting,     setResetting]    = useState(false);
  const [toast,         setToast]        = useState(null);
  const [fieldErrors,   setFieldErrors]  = useState({});
  const [cooldown,      setCooldown]     = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => () => clearInterval(intervalRef.current), []);

  function showToast(msg, type = 'error') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 6000);
  }

  function startCooldown() {
    setCooldown(RESEND_COOLDOWN_S);
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) { clearInterval(intervalRef.current); return 0; }
        return c - 1;
      });
    }, 1000);
  }

  async function handleSendCode(e) {
    e && e.preventDefault();
    if (!email.trim())                      { setFieldErrors({ email: t('auth_err_email_req') }); return; }
    if (!/\S+@\S+\.\S+/.test(email))        { setFieldErrors({ email: t('auth_err_email_inv') }); return; }
    setFieldErrors({});
    setSending(true);
    setToast(null);
    try {
      await sendVerificationCode(email.trim(), 'reset');
      setStep('reset');
      startCooldown();
    } catch (err) {
      showToast(err.message || 'Could not send the code. Please try again.');
    } finally {
      setSending(false);
    }
  }

  function handleChangeEmail() {
    clearInterval(intervalRef.current);
    setStep('email');
    setCode('');
    setCooldown(0);
    setFieldErrors({});
  }

  function validateReset() {
    const errors = {};
    if (!/^\d{6}$/.test(code))            errors.code     = t('auth_enter_code');
    if (!newPassword)                     errors.password = t('auth_err_pass_req');
    else if (newPassword.length < 6)     errors.password = t('auth_err_pass_min');
    if (!confirm)                         errors.confirm  = t('auth_err_conf_req');
    else if (confirm !== newPassword)    errors.confirm  = t('auth_err_conf_match');
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleReset(e) {
    e.preventDefault();
    if (!validateReset()) return;
    setResetting(true);
    setToast(null);
    try {
      await resetPasswordWithCode(email.trim(), code, newPassword);
      setStep('done');
    } catch (err) {
      showToast(err.message || 'Could not reset the password. Please try again.');
    } finally {
      setResetting(false);
    }
  }

  const strength = getPasswordStrength(newPassword);

  // ── Success state ────────────────────────────────────────────
  if (step === 'done') {
    return (
      <AuthLayout>
        <div className="af-success-box">
          <div className="af-success-icon">
            <svg viewBox="0 0 24 24" fill="none" width="30" height="30" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6L9 17l-5-5"/>
            </svg>
          </div>
          <h2 className="af-success-title">{t('auth_reset_done_title')}</h2>
          <p className="af-success-sub">{t('auth_reset_done_sub')}</p>
          <Link to="/login" className="af-back-link">{t('auth_back_login')}</Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div>
        {/* Header */}
        <div className="af-header">
          <h2 className="af-title">{t('auth_forgot_title')}</h2>
          <p className="af-subtitle">
            {step === 'email' ? t('auth_forgot_sub') : t('auth_reset_step_sub')}
          </p>
        </div>

        {/* Toast */}
        {toast && (
          <div className={`af-toast af-toast--${toast.type}`} role="alert">
            <span className="af-toast__icon">{toast.type === 'error' ? '⚠' : '✓'}</span>
            {toast.msg}
          </div>
        )}

        {usePhone ? (
          // ── RESET BY PHONE ────────────────────────────────────────────────
          // The panel owns the whole thing: number, code, then the new
          // password. `onDone` fires once it is finished, at which point the
          // reader is SIGNED IN — so this goes to the dashboard, not to /login,
          // which would bounce them straight back out again.
          <>
            <PhoneAuthPanel
              mode={PHONE_MODES.RESET}
              onDone={() => navigate('/dashboard', { replace: true })}
              onCancel={() => setUsePhone(false)}
            />
            <button type="button" className="af-alt-link" onClick={() => setUsePhone(false)}>
              {t('auth_forgot_use_email')}
            </button>
          </>
        ) : step === 'email' ? (
          // ── Step 1: email ──────────────────────────────────────────
          <form onSubmit={handleSendCode} noValidate>
            <div className="af-field">
              <label className="af-label" htmlFor="fp-email">{t('auth_email_addr_label')}</label>
              <div className="af-input-wrap">
                <span className="af-input-icon">
                  <svg viewBox="0 0 24 24" fill="none" width="16" height="16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                </span>
                <input
                  id="fp-email"
                  className={`af-input${fieldErrors.email ? ' af-input--error' : ''}`}
                  type="email"
                  placeholder={t('auth_email_ph')}
                  value={email}
                  onChange={e => { setEmail(e.target.value); setFieldErrors(p => ({ ...p, email: '' })); }}
                  autoComplete="email"
                  disabled={sending}
                  autoFocus
                />
              </div>
              {fieldErrors.email && <p className="af-field-error">{fieldErrors.email}</p>}
            </div>

            <button className="af-btn" type="submit" disabled={sending}>
              {sending && <span className="af-spinner" />}
              {sending ? t('auth_sending_code') : t('auth_send_code')}
            </button>

            {/* Offered on the FIRST step only. Once a code has been emailed,
                switching method would silently abandon it. */}
            <button type="button" className="af-alt-link"
              onClick={() => setUsePhone(true)} disabled={sending}>
              {t('auth_forgot_use_phone')}
            </button>
          </form>
        ) : (
          // ── Step 2: code + new password (one combined submit) ──────
          <>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20,
              padding: '10px 14px', borderRadius: 10,
              background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.2)',
            }}>
              <span style={{ fontSize: '0.8rem', color: 'var(--site-text-2)', flex: 1 }}>
                {t('auth_code_sent_to')} <strong style={{ color: 'var(--site-text-1)' }}>{email.trim()}</strong>
              </span>
              <button
                type="button"
                onClick={handleChangeEmail}
                style={{ background: 'none', border: 'none', color: '#6366F1', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                {t('auth_change_email')}
              </button>
            </div>

            <form onSubmit={handleReset} noValidate>
              {/* Code */}
              <div className="af-field">
                <label className="af-label" htmlFor="fp-code">{t('auth_code_label')}</label>
                <div className="af-input-wrap">
                  <input
                    id="fp-code"
                    className={`af-input af-input--no-icon${fieldErrors.code ? ' af-input--error' : ''}`}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="123456"
                    value={code}
                    onChange={e => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setFieldErrors(p => ({ ...p, code: '' })); }}
                    disabled={resetting}
                    autoFocus
                  />
                </div>
                {fieldErrors.code && <p className="af-field-error">{fieldErrors.code}</p>}
                <button
                  type="button"
                  className="af-forgot"
                  onClick={handleSendCode}
                  disabled={sending || cooldown > 0}
                  style={{ marginTop: 8 }}
                >
                  {cooldown > 0 ? `${t('auth_resend_in')} ${cooldown}s` : t('auth_resend_code')}
                </button>
              </div>

              {/* New Password */}
              <div className="af-field">
                <label className="af-label" htmlFor="fp-password">{t('auth_password_label')}</label>
                <div className="af-input-wrap">
                  <span className="af-input-icon">
                    <svg viewBox="0 0 24 24" fill="none" width="16" height="16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0110 0v4"/>
                    </svg>
                  </span>
                  <input
                    id="fp-password"
                    className={`af-input af-input--has-action${fieldErrors.password ? ' af-input--error' : ''}`}
                    type={showPass ? 'text' : 'password'}
                    placeholder={t('auth_password_min_ph')}
                    value={newPassword}
                    onChange={e => { setNewPassword(e.target.value); setFieldErrors(p => ({ ...p, password: '' })); }}
                    autoComplete="new-password"
                    disabled={resetting}
                  />
                  <button type="button" className="af-input-action" onClick={() => setShowPass(s => !s)} tabIndex={-1}>
                    {showPass ? <EyeClosed /> : <EyeOpen />}
                  </button>
                </div>
                {fieldErrors.password && <p className="af-field-error">{fieldErrors.password}</p>}

                {newPassword.length > 0 && (
                  <div className="af-strength">
                    <div className="af-strength-bars">
                      {[1, 2, 3, 4].map(n => (
                        <div
                          key={n}
                          className="af-strength-bar"
                          style={{ background: strength.level >= n + 1 ? strength.color : 'var(--site-border-2)' }}
                        />
                      ))}
                    </div>
                    <span className="af-strength-label" style={{ color: strength.level > 1 ? strength.color : 'var(--site-text-3)' }}>
                      {t(STRENGTH_KEYS[strength.level])}
                    </span>
                  </div>
                )}
              </div>

              {/* Confirm New Password */}
              <div className="af-field">
                <label className="af-label" htmlFor="fp-confirm">{t('auth_confirm_label')}</label>
                <div className="af-input-wrap">
                  <span className="af-input-icon">
                    <svg viewBox="0 0 24 24" fill="none" width="16" height="16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </svg>
                  </span>
                  <input
                    id="fp-confirm"
                    className={`af-input af-input--has-action${fieldErrors.confirm ? ' af-input--error' : ''}`}
                    type={showConfirm ? 'text' : 'password'}
                    placeholder={t('auth_confirm_ph')}
                    value={confirm}
                    onChange={e => { setConfirm(e.target.value); setFieldErrors(p => ({ ...p, confirm: '' })); }}
                    autoComplete="new-password"
                    disabled={resetting}
                  />
                  <button type="button" className="af-input-action" onClick={() => setShowConfirm(s => !s)} tabIndex={-1}>
                    {showConfirm ? <EyeClosed /> : <EyeOpen />}
                  </button>
                </div>
                {fieldErrors.confirm && <p className="af-field-error">{fieldErrors.confirm}</p>}
              </div>

              <button className="af-btn" type="submit" disabled={resetting}>
                {resetting && <span className="af-spinner" />}
                {resetting ? t('auth_resetting') : t('auth_reset_password_btn')}
              </button>
            </form>
          </>
        )}

        <p className="af-footer" style={{ marginTop: 24 }}>
          <Link to="/login" className="af-back-link" style={{ justifyContent: 'center' }}>
            {t('auth_back_login')}
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}
